import {
  useState,
  useEffect,
  useRef,
  useCallback,
  TouchEvent as ReactTouchEvent,
  RefObject,
} from 'react';

import { Optional } from '@vimeo/core/types/generic';

import { browserConfig } from '../../configs/BrowserConfig';
import { useThrottledCallback } from '../../hooks/react/useThrottledCallback';

export enum ESwipeDirection {
  RIGHT = 'right',
  LEFT = 'left',
  UP = 'up',
  DOWN = 'down',
}

interface ISwipePoint {
  x: number;
  y: number;
}

export interface ISwipeData {
  direction: Optional<ESwipeDirection>;
  offset: number;
  start: ISwipePoint;
  end: ISwipePoint;
}

export interface IUseSwipeProps {
  ref: RefObject<HTMLElement | null>;
  threshold?: number;
  dependencies?: Array<unknown>;
}

export function useSwipe({ ref, threshold = 5, dependencies = [] }: IUseSwipeProps): ISwipeData {
  const [direction, setDirection] = useState<ESwipeDirection | null>(null);
  const [offset, setOffset] = useState<number>(0);

  const isTouchDevice = browserConfig.BROWSER?.isMobile;

  const start = useRef({ x: 0, y: 0 });
  const end = useRef({ x: 0, y: 0 });

  const dispose = useCallback(() => {
    start.current = { x: 0, y: 0 };
    end.current = { x: 0, y: 0 };
    setDirection(null);
    setOffset(0);
  }, []);

  const onRecalculateDragPosition = useCallback(() => {
    if (
      Math.abs(end.current.x - start.current.x) > Math.abs(end.current.y - start.current.y) &&
      Math.abs(end.current.x - start.current.x) > threshold
    ) {
      setDirection(end.current.x > start.current.x ? ESwipeDirection.RIGHT : ESwipeDirection.LEFT);
      setOffset(Math.ceil(Math.abs(end.current.x - start.current.x)));
    } else if (Math.abs(end.current.y - start.current.y) > threshold) {
      setDirection(end.current.y > start.current.y ? ESwipeDirection.DOWN : ESwipeDirection.UP);
      setOffset(Math.ceil(Math.abs(end.current.y - start.current.y)));
    }
  }, [threshold]);

  const onTouchMove = useThrottledCallback(
    (event: ReactTouchEvent | TouchEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.changedTouches && event.changedTouches.length > 0) {
        end.current.x = event.changedTouches[0].clientX;
        end.current.y = event.changedTouches[0].clientY;
      }

      onRecalculateDragPosition();
    },
    16,
    [onRecalculateDragPosition],
  );

  const onTouchEnd = useCallback(
    (event: ReactTouchEvent | TouchEvent) => {
      if (event.changedTouches && event.changedTouches.length > 0) {
        end.current.x = event.changedTouches[0].clientX;
        end.current.y = event.changedTouches[0].clientY;
      }

      onRecalculateDragPosition();

      dispose();

      const currentElement = ref.current!;

      if (currentElement) {
        currentElement.removeEventListener('touchmove', onTouchMove);
        currentElement.removeEventListener('touchend', onTouchEnd);
        currentElement.removeEventListener('touchcancel', onTouchEnd);
      }
    },
    [dispose, onRecalculateDragPosition, onTouchMove, ref],
  );

  const onTouchStart = useCallback(
    (event: ReactTouchEvent | TouchEvent) => {
      if (event.changedTouches && event.changedTouches.length > 0) {
        start.current.x = event.changedTouches[0].clientX;
        start.current.y = event.changedTouches[0].clientY;
      }

      const currentElement = ref.current!;

      currentElement.addEventListener('touchmove', onTouchMove, { passive: false });
      currentElement.addEventListener('touchend', onTouchEnd);
      currentElement.addEventListener('touchcancel', onTouchEnd);
    },
    [onTouchEnd, onTouchMove, ref],
  );

  useEffect(() => {
    if (ref && ref.current) {
      const currentElement = ref.current;

      if (isTouchDevice) {
        currentElement.addEventListener('touchstart', onTouchStart);
        return () => {
          dispose();

          currentElement.removeEventListener('touchstart', onTouchStart);
          currentElement.removeEventListener('touchmove', onTouchMove);
          currentElement.removeEventListener('touchend', onTouchEnd);
          currentElement.removeEventListener('touchcancel', onTouchEnd);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispose, isTouchDevice, onTouchStart, ref, ...dependencies]);

  return { direction, offset, start: start.current, end: end.current };
}
